import * as Hyphen from "hyphen";
import enUsPatterns from "hyphen/patterns/en-us.js";

let hyphenator: any = null;

// Initialize hyphenator with error handling
const initializeHyphenator = (): any => {
  if (hyphenator) {
    return hyphenator;
  }

  try {
    hyphenator = Hyphen.default
      ? Hyphen.default(enUsPatterns)
      : new (Hyphen as any)(enUsPatterns);
    return hyphenator;
  } catch (error) {
    console.warn("Failed to initialize hyphenator:", error);
    return null;
  }
};

export const addSyllableBreaks = (text: string): string => {
  if (!text || typeof text !== "string") {
    return text || "";
  }

  const hyph = initializeHyphenator();
  if (!hyph) {
    return text; // Fallback to original text if hyphenator fails
  }

  return text
    .split(/(\s+|[,&\-/\\]+)/) // Include more separators like dash, slash
    .map((segment) => {
      // Skip whitespace and punctuation
      if (/^\s+$/.test(segment) || /^[,&\-/\\]+$/.test(segment)) {
        return segment;
      }

      // Don't hyphenate very short words
      if (segment.length <= 4) {
        return segment;
      }

      // Don't hyphenate numbers or mixed alphanumeric
      if (/^\d+$/.test(segment) || /\d/.test(segment)) {
        return segment;
      }

      // Don't hyphenate all caps abbreviations
      if (/^[A-Z]{2,}$/.test(segment)) {
        return segment;
      }

      try {
        const hyphenated = hyph(segment);
        // Only return hyphenated version if it actually added hyphens
        // and the result looks reasonable
        if (
          hyphenated.includes("\u00AD") &&
          hyphenated.length > segment.length
        ) {
          return hyphenated;
        }
        return segment;
      } catch (error) {
        console.warn("Syllable breaking failed for:", segment, error);
        return segment;
      }
    })
    .join("");
};

export const breakTableNames = (tableNames: string): string => {
  if (!tableNames || typeof tableNames !== "string") {
    return tableNames || "";
  }

  // Special handling for table names which often have numbers and abbreviations
  const processed = tableNames
    .split(/(\s*[,&]\s*)/) // Split on commas and ampersands with optional whitespace
    .map((segment, index) => {
      // Keep separators as-is
      if (index % 2 === 1) {
        return segment;
      }

      // For table names, be more conservative with hyphenation
      if (segment.trim().length <= 6) {
        return segment;
      }

      return addSyllableBreaks(segment);
    })
    .join("");

  return processed;
};

export const breakZoneNames = (zoneNames: string): string => {
  if (!zoneNames || typeof zoneNames !== "string") {
    return zoneNames || "";
  }

  // Zone names are often shorter and more descriptive
  return addSyllableBreaks(zoneNames);
};

// Utility function to remove soft hyphens for display purposes
export const removeSoftHyphens = (text: string): string => {
  if (!text || typeof text !== "string") {
    return text || "";
  }
  return text.replace(/\u00AD/g, "");
};

// Utility function to check if text contains soft hyphens
export const hasSoftHyphens = (text: string): boolean => {
  if (!text || typeof text !== "string") {
    return false;
  }
  return text.includes("\u00AD");
};

// For CSS word-break, convert soft hyphens to CSS break opportunities
export const convertToWordBreak = (text: string): string => {
  if (!text || typeof text !== "string") {
    return text || "";
  }

  // Replace soft hyphens with zero-width space for CSS word-break
  return text.replace(/\u00AD/g, "\u200B");
};
